import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FadeIn, StaggeredGrid } from "@/components/animated";
import { bundlesApi } from "@/features/bundles/api/bundlesApi";
import { Card, CardContent, Badge, Skeleton } from "@learn-me/ui";
import { formatCurrency } from "@learn-me/utils";
import { Package, BookOpen, ArrowRight, Percent } from "lucide-react";
import { normalizeImageUrl } from "@/lib/imageUrl";

export function BundlesSection() {
  const { data: bundles, isLoading } = useQuery({
    queryKey: ["bundles"],
    queryFn: () => bundlesApi.list(),
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <Skeleton className="h-8 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-80 w-full" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!bundles || bundles.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-white">
      <div className="container">
        <FadeIn>
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold-100 text-gold-600 mb-4">
              <Package className="h-6 w-6" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Paquetes de Cursos
            </h2>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              Ahorra con nuestros paquetes especiales. Obtén acceso a múltiples
              cursos a un precio preferencial
            </p>
          </div>
        </FadeIn>

        <StaggeredGrid
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          staggerDelay={0.1}
        >
          {bundles.map((bundle) => (
            <motion.div
              key={bundle.id}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <Link to={`/bundles/${bundle.slug}`}>
                <Card className="h-full group overflow-hidden border-2 hover:border-primary-300 transition-all duration-300">
                  {/* Header con imagen */}
                  <div className="relative aspect-[16/9] bg-gradient-to-br from-primary-500 to-primary-700 overflow-hidden">
                    {bundle.thumbnailUrl ? (
                      <img
                        src={normalizeImageUrl(bundle.thumbnailUrl) ?? ""}
                        alt={bundle.name}
                        className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-16 w-16 text-white/50" />
                      </div>
                    )}

                    {/* Badge de descuento */}
                    {bundle.hasDiscount && (
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-green-500 text-white font-bold px-3 py-1.5 flex items-center gap-1">
                          <Percent className="h-3.5 w-3.5" />
                          ¡Oferta!
                        </Badge>
                      </div>
                    )}

                    {/* Cantidad de cursos */}
                    <div className="absolute bottom-3 left-3">
                      <Badge className="bg-black/60 text-white backdrop-blur-sm">
                        <BookOpen className="h-3.5 w-3.5 mr-1" />
                        {bundle.coursesCount} cursos
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-5">
                    <h3 className="font-semibold text-lg text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {bundle.name}
                    </h3>

                    {bundle.shortDescription && (
                      <p className="text-secondary-500 text-sm line-clamp-2 mb-4">
                        {bundle.shortDescription}
                      </p>
                    )}

                    {/* Precios */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-2">
                        {bundle.hasDiscount && bundle.originalPrice ? (
                          <>
                            <span className="text-sm text-secondary-400 line-through">
                              {formatCurrency(
                                bundle.originalPrice,
                                bundle.currency,
                              )}
                            </span>
                            <span className="text-xl font-bold text-green-600">
                              {formatCurrency(
                                bundle.price ?? 0,
                                bundle.currency,
                              )}
                            </span>
                          </>
                        ) : bundle.price ? (
                          <span className="text-xl font-bold text-primary-600">
                            {formatCurrency(bundle.price, bundle.currency)}
                          </span>
                        ) : (
                          <span className="text-lg font-semibold text-secondary-600">
                            Consultar precio
                          </span>
                        )}
                      </div>

                      <span className="flex items-center text-sm font-medium text-primary-600 group-hover:translate-x-1 transition-transform">
                        Ver detalle
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </StaggeredGrid>
      </div>
    </section>
  );
}
